/**
 * Registro central de habilidades de la guía "Clases y puntos".
 * skill_id e item_id son identificadores DISTINTOS y se guardan por separado.
 *
 * idSource:
 * - 'staff': skill_id e item_id facilitados por el staff tras revisar la exportación (11/10/2026).
 * - 'estandar': skill_id estándar de Season 6. Solo se usa para BUSCAR en la exportación;
 *   la relación únicamente se publica si un ítem compatible con la clase tiene ese mismo skill_id.
 */
export type SkillDef = {
  name: string
  skillId: number
  idSource: 'staff' | 'estandar'
  /** Ítem de aprendizaje indicado por el staff (01_items, texto exacto "sección,índice"). */
  learnItemId?: string
  /** Otros nombres documentados del mismo poder (no se usan para unir poderes distintos). */
  aliases?: string[]
}

type RawSkill = Omit<SkillDef, 'idSource'> & { idSource?: SkillDef['idSource'] }

const raw: RawSkill[] = [
  // Staff · 11/10/2026
  { name: 'Fire Burst', skillId: 61, idSource: 'staff', learnItemId: '12,21', aliases: ['FireBurst'] },
  { name: 'Increase Critical Damage', skillId: 64, idSource: 'staff', learnItemId: '12,23', aliases: ['Critical Damage'] },
  { name: 'Heal', skillId: 26, idSource: 'staff', learnItemId: '12,8', aliases: ['Healing'] },
  { name: 'Chain Lightning', skillId: 215, idSource: 'staff', learnItemId: '15,19' },
  { name: 'Drain Life', skillId: 214, idSource: 'staff', learnItemId: '15,20' },
  { name: 'Damage Reflection', skillId: 217, idSource: 'staff', learnItemId: '15,22' },
  { name: 'Sleep', skillId: 219, idSource: 'staff', learnItemId: '15,24' },
  { name: 'Weakness', skillId: 221, idSource: 'staff', learnItemId: '15,26' },
  { name: 'Innovation', skillId: 222, idSource: 'staff', learnItemId: '15,27' },
  { name: 'Chain Drive', skillId: 262, idSource: 'staff', learnItemId: '15,30', aliases: ['Chain Driver'] },
  { name: 'Dark Side', skillId: 263, idSource: 'staff', learnItemId: '15,31' },
  { name: 'Dragon Lore', skillId: 264, idSource: 'staff', learnItemId: '15,32' },
  // skill_id estándar de Season 6: se contrasta con la exportación antes de publicar nada.
  { name: 'Twisting Slash', skillId: 41 },
  { name: 'Death Stab', skillId: 43 },
  { name: 'Greater Fortitude', skillId: 48, aliases: ['Swell Life'] },
  { name: 'Evil Spirit', skillId: 9 },
  { name: 'Aqua Beam', skillId: 12 },
  { name: 'Ice', skillId: 7 },
  { name: 'Teleport', skillId: 6 },
  { name: 'Triple Shot', skillId: 24 },
  { name: 'Penetration', skillId: 52 },
  { name: 'Ice Arrow', skillId: 51 },
  { name: 'Greater Damage', skillId: 28 },
  { name: 'Greater Defense', skillId: 27 },
  { name: 'Power Slash', skillId: 56 },
  { name: 'Fire Slash', skillId: 55 },
  { name: 'Fire Scream', skillId: 78 },
  { name: 'Summon', skillId: 63 },
  { name: 'Earthquake', skillId: 62 },
]

export const skillDefs: SkillDef[] = raw.map((s) => ({ ...s, idSource: s.idSource ?? 'estandar' }))

const compact = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '')

export const skillDefByName = (name: string) => {
  const n = compact(name)
  return skillDefs.find((s) => compact(s.name) === n || s.aliases?.some((a) => compact(a) === n))
}

/** Solo los skill_id facilitados por el staff dan nombre a un ítem; los estándar necesitan un ítem con ese mismo skill_id. */
export const skillDefById = (id: number | undefined) => (id ? skillDefs.find((s) => s.skillId === id) : undefined)
